import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { FormSavingOverlay } from '../components/shared/FormSavingOverlay';

export type RequestSaveTransitionValue = {
  activeRequestId: string | null;
  saving: boolean;
  savingMessage: string;
  begin: (requestId: string | number) => void;
  complete: (requestId: string | number) => void;
  setSaving: (saving: boolean) => void;
  setSavingMessage: (message: string) => void;
  hasActiveHandoff: () => boolean;
};

const RequestSaveTransitionContext =
  createContext<RequestSaveTransitionValue | null>(null);

const DEFAULT_SAVING_MESSAGE = 'Saving request…';

export function RequestSaveTransitionProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingMessage, setSavingMessageState] = useState(
    DEFAULT_SAVING_MESSAGE,
  );
  const activeRequestIdRef = useRef<string | null>(null);
  const begin = useCallback((requestId: string | number) => {
    const id = String(requestId);
    activeRequestIdRef.current = id;
    setActiveRequestId(id);
    setSaving(true);
  }, []);
  const complete = useCallback((requestId: string | number) => {
    const id = String(requestId);
    if (activeRequestIdRef.current !== id) {
      return;
    }
    activeRequestIdRef.current = null;
    setActiveRequestId(null);
    setSaving(false);
    setSavingMessageState(DEFAULT_SAVING_MESSAGE);
  }, []);
  const setSavingMessage = useCallback((message: string) => {
    setSavingMessageState(message);
  }, []);
  const hasActiveHandoff = useCallback(
    () => activeRequestIdRef.current != null,
    [],
  );
  const value = useMemo(
    () => ({
      activeRequestId,
      saving,
      savingMessage,
      begin,
      complete,
      setSaving,
      setSavingMessage,
      hasActiveHandoff,
    }),
    [
      activeRequestId,
      saving,
      savingMessage,
      begin,
      complete,
      hasActiveHandoff,
      setSavingMessage,
    ],
  );

  return (
    <RequestSaveTransitionContext.Provider value={value}>
      {children}
      {Platform.OS !== 'web' ? (
        <FormSavingOverlay
          visible={saving || activeRequestId != null}
          message={savingMessage}
        />
      ) : null}
    </RequestSaveTransitionContext.Provider>
  );
}

export function useRequestSaveTransition(): RequestSaveTransitionValue {
  const value = useContext(RequestSaveTransitionContext);
  if (!value) {
    return {
      activeRequestId: null,
      saving: false,
      savingMessage: DEFAULT_SAVING_MESSAGE,
      begin: noop,
      complete: noop,
      setSaving: noopBoolean,
      setSavingMessage: noopString,
      hasActiveHandoff: () => false,
    };
  }
  return value;
}

function noop(): void {}
function noopBoolean(_value: boolean): void {}
function noopString(_value: string): void {}
